import axios from 'axios';
import { storeAction } from '../storeActions.constant';
import { storeGetters } from '../storeGetters.constant';
import { storeMutation } from '../storeMutations.constant';
import { getBasicHeaders } from '../../common/utils/utils';
import { wrapAction } from '../../common/utils/actionLifecycle';

const groupPageState = () => {
    return {
        baseURL: process.env.VUE_APP_BASE_URL,
        group_canEdit: null,
        groupFocusList: [],
        groupConfigRequest: {},
        pendingRequestList: [],
        inviteeList: [],
        toggleRequestedMembers: false,
        toggleRequestedGroups: false,
        toggleInviteSomeone: false,
        joinNetwork: '',
        requestedJoineeList: [],
        resultApproveRequest: null,
        communityAdminList: [],
        isLastAdminStatus: false,
    };
};

const groupPageMutations = {
    [storeMutation.setCommunityAdminList]: (state, data) => {
        data.forEach((i) => {
            i.isAdmin = true;
        });
        state.communityAdminList = data;
    },
    [storeMutation.setGroupFocusList]: (state, data) => {
        state.groupFocusList = data;
    },
    [storeMutation.setGroupConfigRequest]: (state, data) => {
        state.groupConfigRequest = data;
    },
    [storeMutation.setPendingRequestList]: (state, data) => {
        state.pendingRequestList = data && data.canApprove ? data.groupApplicationList : [];
    },
    [storeMutation.toggleRequestedMembers]: (state, data) => {
        state.toggleRequestedMembers = data;
    },
    [storeMutation.toggleRequestedGroups]: (state, data) => {
        state.toggleRequestedGroups = data;
    },
    [storeMutation.toggleInviteSomeone]: (state, data) => {
        state.toggleInviteSomeone = data;
    },
    [storeMutation.setIsLastAdmin]: (state, data) => {
        state.isLastAdminStatus = data;
    },
};

const groupPageActions = {
    [storeAction.getCommunityMembers]: wrapAction(
        storeAction.getCommunityMembers,
        ({ state, rootGetters }, searchToken) => {
            const communityType = rootGetters[storeGetters.communityName];
            return rootGetters[storeGetters.communityIdPromise].then((communityId) => {
                return axios({
                    url: `${
                        state.baseURL
                    }communities/api/communities/member-search/${communityId}/${communityType}/${searchToken}`,
                    headers: getBasicHeaders(),
                    method: 'GET',
                })
                    .then((response) => {
                        return response.data;
                    })
                    .catch((err) => {
                        //TODO: handle error
                        console.log(err);
                        throw err;
                    });
            });
        },
    ),

    [storeAction.getGroupConfiguration]: async ({ state, commit, rootGetters }) => {
        return rootGetters[storeGetters.communityIdPromise].then((groupId) => {
            return axios({
                url: `${state.baseURL}communities/api/communities/group/${groupId}`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {
                    commit(storeMutation.setGroupConfigRequest, response.data);
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        });
    },

    [storeAction.getPendingRequestList]: async ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}communities/api/communities/grouprequests/pendinlist`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setPendingRequestList, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },

    [storeAction.getFocusList]: async ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}communities/api/communities/groupform/getfocuslist`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setGroupFocusList, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.toggleGroupMemberAdminPermission]: async ({ state, rootGetters }, data) => {
        return rootGetters[storeGetters.communityIdPromise].then((communityId) => {
            return axios({
                url: `${state.baseURL}communities/api/groups/add-admin/${communityId}/${data.memberId}/${data.isAdmin}`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        });
    },
    [storeAction.removeGroupMember]: async ({ state, rootGetters }, memberId) => {
        return rootGetters[storeGetters.communityIdPromise].then((communityId) => {
            return axios({
                url: `${state.baseURL}communities/api/groups/${communityId}/remove-member/${memberId}`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        });
    },
    [storeAction.reviewRequest]: ({ state }, groupObject) => {
        return axios({
            url: `${state.baseURL}communities/api/communities/group/reviewcreaterequest`,
            headers: getBasicHeaders(),
            data: groupObject,
            method: 'POST',
        })
            .then((response) => {
                state.pendingRequestList = state.pendingRequestList.filter(g => g.groupId !== groupObject.groupId);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.requestGroupPost]: ({ state }, data) => {
        return axios({
            url: `${state.baseURL}communities/api/communities/group/${
                data.isNewRequestGroup ? 'creategrouprequest' : 'configure'
            }`,
            headers: getBasicHeaders(),
            data: data.groupObject,
            method: 'POST',
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.getAllCommunityAdminList]: async ({ state, commit, rootGetters }) => {
        const communityType = rootGetters[storeGetters.communityName];
        return rootGetters[storeGetters.communityIdPromise].then((communityId) => {
            return axios({
                url: `${state.baseURL}communities/api/groups/list-admins/${communityId}/${communityType}`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {
                    commit(storeMutation.setCommunityAdminList, response.data);
                    return response.data;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        });
    },
    //is last admin or not
    [storeAction.getIsLastAdmin]: async ({ state, commit, rootGetters }) => {
        const communityType = rootGetters[storeGetters.communityName];
        return rootGetters[storeGetters.communityIdPromise].then((communityId) => {
            return axios({
                url: `${state.baseURL}communities/api/groups/isLastAdmin/${communityId}/${communityType}`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {                   
                    commit(storeMutation.setIsLastAdmin, response.data);
                    return response.data;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        });
    },
};

const groupPageGetters = {
    [storeGetters.canEditGroup]: state => state.group_canEdit,
    [storeGetters.groupFocusList]: state => state.groupFocusList,
    [storeGetters.groupConfigRequest]: state => state.groupConfigRequest,
    [storeGetters.pendingRequestList]: state => state.pendingRequestList,
    [storeGetters.isLastAdminStatus]: state => state.isLastAdminStatus,
    [storeGetters.relatedCommunitiesList]: state => state.relatedCommunitiesList,
    [storeGetters.requestedJoineeList]: state => state.requestedJoineeList,
    [storeGetters.returnInviteesList]: state => state.returnInviteesList,
    [storeGetters.communityAdminList]: (state) => {
        return state.communityAdminList;
    },
};

export default {
    namespaced: true,
    state: {
        initialState: groupPageState(),
        ...groupPageState(),
    },
    mutations: { ...groupPageMutations },
    actions: { ...groupPageActions },
    getters: { ...groupPageGetters },
};
